import { cssVar } from '@mfw/common';
import type { PaginationProps } from '@mui/material/Pagination';
import type { Theme } from '@mui/material/styles';

import { paginationItemClasses } from '@mui/material/PaginationItem';
import { alpha } from '@mui/material/styles';

import { onSurfaceMark } from '../../onSurface';
import { fillMain } from '../../fillMain';

// ----------------------------------------------------------------------

const COLORS = [
  'primary',
  'accent',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// NEW VARIANT
declare module '@mui/material/Pagination' {
  interface PaginationPropsVariantOverrides {
    soft: true;
  }

  interface PaginationPropsColorOverrides {
    success: true;
    warning: true;
    error: true;
    info: true;
    // The second brand anchor — see `button.tsx`.
    accent: true;
  }
}

// ----------------------------------------------------------------------

export function pagination(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: PaginationProps) => {
    const defaultColor = ownerState.color === 'standard';

    const filledVariant = ownerState.variant === 'text';

    const outlinedVariant = ownerState.variant === 'outlined';

    const softVariant = ownerState.variant === 'soft';

    const defaultStyle = {
      [`& .${paginationItemClasses.root}`]: {
        ...(outlinedVariant && {
          borderColor: alpha(theme.palette.grey[500], 0.24),
        }),

        [`&.${paginationItemClasses.selected}`]: {
          fontWeight: theme.typography.fontWeightSemiBold,
          ...(outlinedVariant && {
            borderColor: 'currentColor',
          }),

          ...(defaultColor && {
            backgroundColor: alpha(theme.palette.grey[500], 0.08),
            // THE BRAND, under the ink the engine derives for it. Unlike the
            // chip — which is passive metadata and went tonal — the current
            // page is a genuine "you are here" marker and has earned a fill.
            //
            // A fill brings its own ink, which is what makes this
            // surface-independent: `primary-contrastText` is
            // `contrastColorForRgb(primary-main)`, i.e. whichever of black or
            // white scores higher, so it cannot drop below 4.58:1 for any
            // brand a tenant picks. (It used to invert against `text.primary`,
            // which made the current page a white pill.)
            //
            // That bound holds for `main` only. Hover and keyboard focus keep
            // it: `.dark` under the same ink read 2.63–2.69:1 on a mid-tone
            // brand, and MUI marks focus by a fill change alone, so focus
            // draws a ring instead (`fill-hover.spec.ts`).
            ...(filledVariant && {
              '&:hover': {
                backgroundColor: cssVar.primary.main,
              },
              [`&.${paginationItemClasses.focusVisible}`]: {
                backgroundColor: cssVar.primary.main,
                outline: `2px solid ${onSurfaceMark.primary}`,
                outlineOffset: 2,
              },
              backgroundColor: cssVar.primary.main,
              color: cssVar.primary.contrastText,
            }),
          }),
        },
      },
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        [`& .${paginationItemClasses.root}`]: {
          [`&.${paginationItemClasses.selected}`]: {
            ...(ownerState.color === color && {
              // FILLED — MUI's own selected fill is `main` under
              // `contrastText`; its hover and focus move to `.dark`. Same
              // answer as the default-colour branch above.
              ...(filledVariant && {
                '&:hover': {
                  backgroundColor: fillMain(theme, color),
                },
                [`&.${paginationItemClasses.focusVisible}`]: {
                  backgroundColor: fillMain(theme, color),
                  outline: `2px solid ${onSurfaceMark[color]}`,
                  outlineOffset: 2,
                },
              }),
              // SOFT
              ...(softVariant && {
                '&:hover': {
                  backgroundColor: alpha(theme.palette[color].main, 0.16),
                },
                backgroundColor: alpha(theme.palette[color].main, 0.08),
                color: theme.palette[color][lightMode ? 'dark' : 'light'],
              }),
            }),
          },
        },
      }),
    }));

    return [defaultStyle, ...colorStyle];
  };

  return {
    MuiPagination: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: PaginationProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
